import { FiAlertTriangle } from "react-icons/fi";
// Admin user page → "Onboarding answers": what they told us in the onboarding
// quiz (components/Onboarding) and the clip plan it promised them.
// Data: user.onboarding (+ .answers, saved by POST /users/me/onboarding).

const LABELS = {
  streamHours: { "1-2": "1–2 hours", "3-5": "3–5 hours", "6+": "6+ hours" },
  streamDays: { "1-2": "1–2 days a week", "3-4": "3–4 days a week", "5-7": "5–7 days a week" },
  postingNow: { "0": "None yet", "1-3": "1–3 a week", "4-10": "4–10 a week", "10+": "10+ a week" },
  clipMethod: {
    none: "Doesn't clip yet", self: "Edits clips themselves", editor: "Pays an editor",
    fans: "Fans or clippers do it", tool: "Uses another AI tool", new: "Just getting started",
  },
  goal: {
    followers: "Grow followers", viral: "Go viral", live: "More viewers on live", money: "Make money from clips",
    pages: "Grow clip pages", clients: "Clip for more streamers",
  },
  struggle: {
    time: "No time to edit", moments: "Doesn't know what will go viral", consistency: "Can't post consistently",
    cost: "Editors too expensive", late: "Other clippers post first",
  },
  streamType: { gaming: "Gaming", talking_head: "Podcast / Talking head", irl: "IRL / Vlog", music: "Music" },
  platform: { twitch: "Twitch", kick: "Kick", youtube: "YouTube", rumble: "Rumble", x: "X", ganjing: "Ganjing World", own: "OWN" },
};

// Onboarding step ids (components/Onboarding) → plain words.
const STEP_LABELS = {
  intro: "Welcome screen", userType: "Streamer or clipper", name: "Name", username: "Channel name",
  platform: "Platform", connect: "Connect channel", hours: "Stream length", days: "Days live",
  postingNow: "Clips posted now", proofClips: "Proof screen", method: "How they clip",
  effort: "Editing time", proofLive: "Live-clipping screen", goal: "Goal", struggle: "Struggle",
  contentType: "Content type", proofResults: "Results screen", photo: "Face photo",
  foundVia: "Where they found us", loading: "Building plan", plan: "Their plan (last screen)",
};

const label = (group, v) => (v ? LABELS[group]?.[v] || v : null);

function Row({ q, a }) {
  return (
    <div className="aph-row">
      <span className="aph-label">{q}</span>
      <span className="aph-value">{a || <span className="muted">—</span>}</span>
    </div>
  );
}

export default function UserOnboarding({ user }) {
  const ob = user.onboarding || {};
  const ans = ob.answers || {};
  const brand = (user.brands || []).find((b) => String(b._id) === String(user.activeBrandId)) || user.brands?.[0];
  const cs = brand?.creatorSettings || user.creatorSettings || {};
  const clipper = ob.userType === "clipper";
  const hasQuiz = Object.values(ans).some((v) => v !== undefined && v !== null && v !== "");

  let effort = null;
  if (ans.effort != null && ans.clipMethod === "self") effort = `${ans.effort} hrs/week editing`;
  if (ans.effort != null && ans.clipMethod === "editor") effort = `$${ans.effort}/week to their editor`;

  // Not finished: show what they answered so far and where they stopped
  // (saved per step by PUT /users/me/onboarding/progress).
  const started = !ob.completed && (ob.userType || hasQuiz || ob.lastStep);
  const stoppedAt = STEP_LABELS[ob.lastStep] || ob.lastStep;
  let body;
  if (!ob.completed && !started) {
    body = <div className="aud-empty-sm">Hasn't started onboarding yet.</div>;
  } else {
    body = (
      <>
      {!ob.completed && (
        <div className="aud-empty-sm" style={{ marginBottom: 12 }}>
          <FiAlertTriangle /> Didn't finish onboarding{stoppedAt ? <> · stopped at <b>{stoppedAt}</b></> : null}
          {ob.progressAt ? ` · ${new Date(ob.progressAt).toLocaleString()}` : ""}
        </div>
      )}
      <div className="aph-grid">
        <div className="aph-card">
          <div className="aph-card-title">Who they are</div>
          <Row q="Role" a={ob.userType ? (clipper ? "Clipper" : "Streamer") : null} />
          <Row q={clipper ? "Streamer they clip" : "Streamer name"} a={brand?.name || user.streamerName} />
          <Row q="Channel" a={cs.channelUsername ? `@${cs.channelUsername} · ${label("platform", cs.platform) || ""}` : null} />
          <Row q="Content" a={label("streamType", cs.streamType)} />
          <Row q="Face photo" a={user.profilePicture ? "Uploaded" : null} />
          <Row q="Found us via" a={ob.foundVia ? (ob.foundVia === "other" ? `Other: ${ob.foundViaOther || "?"}` : ob.foundVia) : null} />
        </div>
        <div className="aph-card">
          <div className="aph-card-title">Their answers</div>
          {hasQuiz ? (
            <>
              <Row q="Stream length" a={label("streamHours", ans.streamHours)} />
              <Row q="Goes live" a={label("streamDays", ans.streamDays)} />
              <Row q="Clips posted now" a={label("postingNow", ans.postingNow)} />
              <Row q="How they clip today" a={label("clipMethod", ans.clipMethod)} />
              {effort && <Row q={ans.clipMethod === "editor" ? "Editor cost" : "Editing time"} a={effort} />}
              <Row q="Goal" a={label("goal", ans.goal)} />
              <Row q="Holding them back" a={label("struggle", ans.struggle)} />
              <Row q="Plan promised" a={ans.clipsPerWeekEstimate ? `≈${ans.clipsPerWeekEstimate} clips a week` : null} />
            </>
          ) : (
            <div className="aud-empty-sm">{ob.completed ? "Onboarded before these questions existed." : "No answers yet."}</div>
          )}
        </div>
      </div>
      </>
    );
  }

  return (
    <div className="aud-section">
      <h2>
        Onboarding answers
        {ob.completedAt && (
          <span className="muted" style={{ fontSize: 13, fontWeight: 500, marginLeft: 8 }}>
            finished {new Date(ob.completedAt).toLocaleString()}
          </span>
        )}
      </h2>
      {body}
    </div>
  );
}
